<script setup lang="ts">
import { useWindowSize } from '@vueuse/core';
import TooltipComponent, { type TooltipData } from '../../tooltip/index.vue';
import type { ScaleLinear } from 'd3';
import type { ScatterChartData } from '../index.vue';

// ================================================ Props
const emit = defineEmits<{
  (e: 'hover-point', index: number): void;
  (e: 'unhover-point'): void;
}>();

const props = defineProps<{
  data: ScatterChartData;
  loading: boolean;
  yScale: ScaleLinear<number, number>;
  xScale: ScaleLinear<number, number>;
  tooltip?: (dataIndex: number) => TooltipData | undefined;
}>();

// ================================================ Data
const tooltipData = ref<TooltipData | null>(null);
const tooltipPosition = ref({
  show: false,
  dataIndex: 0,
  left: 0,
  top: 0,
});

const overlayRef = useTemplateRef<HTMLElement>('overlayRef');
const tooltipRef = useTemplateRef<HTMLElement>('tooltipRef');

// ================================================ Methods
const showTooltip = (pointIndex: number) => {
  if (!props.tooltip || !overlayRef.value) return;

  // Get tooltip data
  tooltipData.value = props.tooltip(pointIndex) ?? null;
  if (!tooltipData.value) return;

  const chartRect = overlayRef.value.getBoundingClientRect();

  // Find the point in our data by its originalIndex or regular index
  const point = props.data.points.find((p, index) => {
    return (p.originalIndex ?? index) === pointIndex;
  });

  if (!point) return;

  const { width: windowWidth } = useWindowSize();

  // Get the exact pixel position of the point
  const pointX = (props.xScale(point.x) / 100) * chartRect.width;
  const pointY = (props.yScale(point.y) / 100) * chartRect.height;

  // Calculate screen position
  const screenX = chartRect.left + pointX;
  const screenY = chartRect.top + pointY;

  // Position tooltip above the point, like in bar chart (page coordinates)
  let tooltipLeft = screenX;
  const tooltipTop = screenY - 12;

  // Ensure tooltip doesn't go off screen horizontally
  const tooltipWidth = 200; // Approximate width
  if (tooltipLeft + tooltipWidth / 2 > windowWidth.value) {
    tooltipLeft = windowWidth.value - tooltipWidth / 2 - 10;
  } else if (tooltipLeft - tooltipWidth / 2 < 10) {
    tooltipLeft = tooltipWidth / 2 + 10;
  }

  // Set tooltip position
  tooltipPosition.value = {
    show: true,
    dataIndex: pointIndex,
    left: tooltipLeft,
    top: tooltipTop,
  };

  emit('hover-point', pointIndex);
};

const hideTooltip = () => {
  tooltipPosition.value.show = false;
  tooltipData.value = null;
  emit('unhover-point');
};

const handleMouseMove = (event: MouseEvent) => {
  if (!overlayRef.value) return;

  const rect = overlayRef.value.getBoundingClientRect();
  const mouseX = event.clientX - rect.left;
  const mouseY = event.clientY - rect.top;

  // Find the closest data point
  let closestIndex = 0;
  let minDistance = Infinity;

  props.data.points.forEach((point, index) => {
    const pointX = (props.xScale(point.x) / 100) * rect.width;
    const pointY = (props.yScale(point.y) / 100) * rect.height;

    const distance = Math.sqrt(Math.pow(pointX - mouseX, 2) + Math.pow(pointY - mouseY, 2));

    if (distance < minDistance) {
      minDistance = distance;
      closestIndex = index;
    }
  });

  // Only show tooltip if mouse is close enough to a point (within 15 pixels)
  if (minDistance > 15) {
    hideTooltip();
    return;
  }

  // Show tooltip for the closest point using original index
  const point = props.data.points[closestIndex];
  const tooltipIndex = point.originalIndex ?? closestIndex;
  showTooltip(tooltipIndex);
};
</script>

<template>
  <div>
    <!-- Invisible overlay for mouse events -->
    <div ref="overlayRef" class="absolute inset-0 w-full h-full z-[25]" @mousemove="handleMouseMove" @mouseleave="hideTooltip" />

    <!-- Tooltip -->
    <transition
      enter-active-class="transition-opacity duration-150"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <Teleport v-if="tooltipPosition.show" :to="'body'">
        <TooltipComponent
          ref="tooltipRef"
          class="fixed z-50 pointer-events-none -translate-x-[50%] -translate-y-[100%]"
          :style="{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
          }"
          :title="tooltipData?.title"
          :subtitle="tooltipData?.subtitle"
          :body="tooltipData?.body"
          :footer="tooltipData?.footer"
          :description="tooltipData?.description"
          arrow-position="bottom"
        />
      </Teleport>
    </transition>
  </div>
</template>
